/**
 * An integration's logo, or its first letter when it has none. A tool with no integration (a lookup
 * the tool server routes itself) shows a plain tool mark. Sized by the caller's class.
 */

import { Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getBrandLogo } from '../constants';

const FALLBACK = 'inline-flex size-5 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-800';

interface IntegrationLogoProps {
	provider: string | null;
	className?: string;
	/** Beside the integration's written name, where naming it again would only repeat it. */
	decorative?: boolean;
}

export const IntegrationLogo = ({ provider, className, decorative }: IntegrationLogoProps) => {
	const hidden = decorative || undefined;
	if (!provider?.trim()) {
		return (
			<span
				role={decorative ? undefined : 'img'}
				aria-label={decorative ? undefined : 'No integration'}
				aria-hidden={hidden}
				className={cn(FALLBACK, className)}
			>
				<Wrench className='size-3' />
			</span>
		);
	}
	const logo = getBrandLogo(provider);
	return logo ? (
		<span
			aria-hidden={hidden}
			className={cn(
				'inline-flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-md border border-gray-500 bg-white p-0.5',
				className
			)}
		>
			<img src={logo} alt={decorative ? '' : provider} className='size-full object-contain' />
		</span>
	) : (
		<span
			role={decorative ? undefined : 'img'}
			aria-label={decorative ? undefined : provider}
			aria-hidden={hidden}
			className={cn(FALLBACK, 'text-[10px] font-semibold', className)}
		>
			{provider.trim().charAt(0).toUpperCase()}
		</span>
	);
};
